/**
 * Lesson copy personalization layer.
 *
 * Adapts a lesson's concept paragraphs to the current user:
 *  - tone shifts by experience mode (basics → friendly framing; improve → tactical framing)
 *  - prepends a position-aware "What this means for you" line on the last concept
 *  - resolves placeholder tokens: {sport}, {Sport}, {position}, {Position}
 *
 * The original concept text is preserved verbatim — we only WRAP it with
 * extra context. If no personalization signal is available, the original text
 * is returned unchanged.
 */

import type { ExperienceMode } from '@/hooks/usePreferredSport';
import type { SkillLevel } from '@/hooks/useSportSkillLevel';

export interface PersonalizeContext {
  /** coarse, account-wide preference (basics | improve) */
  experienceMode: ExperienceMode;
  /** active sport on the home tab — used for {sport} substitution & framing */
  sport: string;
  /** position from player_profiles, e.g. "QB", "Striker", "Point Guard". null for adults */
  position?: string | null;
  /** the sport this lesson is actually about (derived from the lesson id prefix) */
  lessonSport: string;
  /** richer per-sport level for the LESSON's sport, when the user has set one */
  lessonSportLevel?: SkillLevel | null;
}

/**
 * Resolves the effective tone for a given context.
 * Per-sport level wins when present; otherwise we fall back to the coarse
 * experience-mode signal so we always produce *some* personalization.
 */
type Tone = 'rookie' | 'casual' | 'pro';
function resolveTone(ctx: PersonalizeContext): Tone {
  if (ctx.lessonSportLevel) {
    if (ctx.lessonSportLevel === 'advanced') return 'pro';
    if (ctx.lessonSportLevel === 'intermediate') return 'casual';
    return 'rookie';
  }
  return ctx.experienceMode === 'improve' ? 'pro' : 'rookie';
}


const SPORT_LABELS: Record<string, string> = {
  football: 'football',
  soccer: 'soccer',
  basketball: 'basketball',
  baseball: 'baseball',
};

const ROOKIE_OPENERS = [
  "Here's the simple version:",
  'Quick framing first:',
  'In plain words:',
];

const CASUAL_OPENERS = [
  'Quick refresher:',
  'Building on what you know:',
  "Here's what's happening:",
];

const PRO_OPENERS = [
  'Tactical lens:',
  "Coach's-eye view:",
  'Strategic takeaway:',
];

function applyTokens(text: string, ctx: PersonalizeContext): string {
  const sportLabel = SPORT_LABELS[ctx.sport] ?? ctx.sport;
  const positionLabel = ctx.position?.trim() || '';
  return text
    .replace(/\{sport\}/g, sportLabel)
    .replace(/\{Sport\}/g, sportLabel.charAt(0).toUpperCase() + sportLabel.slice(1))
    .replace(/\{position\}/g, positionLabel || 'your position')
    .replace(/\{Position\}/g, positionLabel || 'Your position');
}

function pickStable<T>(arr: T[], seed: string): T {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return arr[h % arr.length];
}

function openersForTone(tone: Tone): string[] {
  if (tone === 'pro') return PRO_OPENERS;
  if (tone === 'casual') return CASUAL_OPENERS;
  return ROOKIE_OPENERS;
}

/**
 * Returns the personalized text for a single concept paragraph.
 * Pure function — safe to memoize.
 */
export function personalizeConcept(
  text: string,
  ctx: PersonalizeContext,
  conceptIndex: number,
  totalConcepts: number,
  lessonId: string,
): string {
  const base = applyTokens(text, ctx);
  const isFirst = conceptIndex === 0;
  const isLast = conceptIndex === totalConcepts - 1;
  const tone = resolveTone(ctx);

  let out = base;
  if (isFirst && totalConcepts > 1) {
    out = `${pickStable(openersForTone(tone), lessonId)} ${out}`;
  }

  if (
    isLast &&
    ctx.position &&
    ctx.position.trim().length > 0 &&
    ctx.lessonSport === ctx.sport
  ) {
    const pos = ctx.position.trim();
    const tail =
      tone === 'pro'
        ? ` Read this through a ${pos}'s lens — where do you sit on the field when this happens?`
        : tone === 'casual'
          ? ` Think about how a ${pos} would react to this in a real game.`
          : ` As a ${pos}, ask yourself: when does this come up in my game?`;
    out = `${out}${tail}`;
  }

  return out;
}

/** Human label for the active tone — used by the "Adapted for…" badge. */
export function toneLabel(ctx: PersonalizeContext): string {
  const tone = resolveTone(ctx);
  if (tone === 'pro') return 'Tactical view';
  if (tone === 'casual') return 'Building on basics';
  return 'Rookie-friendly';
}


/** True when any personalization will actually take effect for this context. */
export function isPersonalizationActive(ctx: PersonalizeContext, totalConcepts: number): boolean {
  if (totalConcepts > 1) return true; // tone opener always fires
  if (ctx.position && ctx.lessonSport === ctx.sport) return true;
  return false;
}
